import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { Icon, IconSize } from '@shared/icons';
import { useInventoryStockLevels } from '@features/inventory';
import { APP_CONFIG } from '../../../../config/app.config';
import DashboardCardHeader from '../general/DashboardCardHeader';
import DashboardCardContent from '../general/DashboardCardContent';

import { ProductFilter } from '@domain/product/models/ProductFilter';

export const DASHBOARD_PRODUCT_FILTER: ProductFilter = {
  purchasable: true,
  active: true,
};


const InventoryKpiCard: React.FC = () => {
  const colors = APP_CONFIG.ui.theme.colors;
  const { stockLevels, isLoading } = useInventoryStockLevels(
    DASHBOARD_PRODUCT_FILTER,
  );

  const inventoryStats = stockLevels ?? {
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
    total: 0,
  };
  const barTotal = Math.max(inventoryStats.total, 1);

  return (
    <Stack minWidth={0} overflow="hidden">
      <DashboardCardHeader icon="Package01" title="Inventory">
        <Link to="/inventory">
          <Icon icon="LinkExternal" size={IconSize.SMALL} color={colors.primaryText} />
        </Link>
      </DashboardCardHeader>

      <DashboardCardContent>
        {isLoading && !stockLevels ? (
          <Stack alignItems="center" justifyContent="center" height={58}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <Stack gap={1}>
            <Stack direction="row" gap={2}>
              {[
                { value: inventoryStats.inStock, label: 'In stock' },
                { value: inventoryStats.lowStock, label: 'Low stock' },
                { value: inventoryStats.outOfStock, label: 'Out of stock' },
              ].map((stat) => (
                <Box key={stat.label} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: 28,
                      lineHeight: '36px',
                      color: colors.primaryText,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '16px',
                      letterSpacing: '0.5px',
                      color: colors.secondaryText,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Stack
              direction="row"
              gap="2px"
              height={12}
              sx={{ borderRadius: '16px', overflow: 'hidden' }}
            >
              <Box
                sx={{
                  flex: inventoryStats.inStock / barTotal,
                  minWidth: inventoryStats.inStock > 0 ? 4 : 0,
                  bgcolor: colors.primary,
                  borderRadius: '12px',
                  border: `1px solid ${colors.background}`,
                }}
              />
              <Box
                sx={{
                  flex: inventoryStats.lowStock / barTotal,
                  minWidth: inventoryStats.lowStock > 0 ? 4 : 0,
                  bgcolor: colors.warning,
                  borderRadius: '12px',
                  border: `1px solid ${colors.background}`,
                }}
              />
              <Box
                sx={{
                  flex: inventoryStats.outOfStock / barTotal,
                  minWidth: inventoryStats.outOfStock > 0 ? 4 : 0,
                  bgcolor: colors.error,
                  borderRadius: '12px',
                  border: `1px solid ${colors.background}`,
                }}
              />
            </Stack>
          </Stack>
        )}
      </DashboardCardContent>
    </Stack>
  );
};

export default InventoryKpiCard;
